import { useCallback } from 'react';

import { ia } from 'lib/helpers/utility';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';

import EmptyProviderState from './EmptyProviderState';
import { getProviderColumns } from './lib/providerColumns';
import { PROVIDER_FILTERS } from './lib/providerFilters';

const EMPTY_STATE_HEIGHT = 336;
const MAX_HEIGHT = 400;

const ProvidersTable = ({
  emptyTitle,
  emptyDescription,
  showingRenderingBillingProviders,
  showStates,
  addProvider,
  enableSelection = !!addProvider,
  noFiltersAndDisplayButton = !enableSelection,
  providers,
  onRemove,
  fieldName,
  addProviderButton,
  setSelectedRows,
  rowSelection = 'multiple',
  stateOptions,
  onOpenStatesModal,
  highlightedIds,
  ...props
}) => {
  const rowCount = providers?.length ?? 0;
  const ROW_HEIGHT = showStates ? 60 : 42;
  const HEADER_HEIGHT = addProvider ? 80 : 40;
  const dynamicHeight = Math.min(
    rowCount > 0 ? HEADER_HEIGHT + rowCount * ROW_HEIGHT : HEADER_HEIGHT + EMPTY_STATE_HEIGHT,
    MAX_HEIGHT
  );

  const isStatesEnabled = Boolean(showStates);
  const isInNetwork = fieldName === 'in_network_providers';

  const defaultColumns = getProviderColumns({
    enableSelection,
    showingRenderingBillingProviders,
    showStates: isStatesEnabled && ia(stateOptions),
    onRemove,
    stateOptions,
    onOpenStatesModal,
    isInNetwork
  });

  const handleSelect = (selectedNodes) => {
    if (!setSelectedRows) return;
    setSelectedRows(selectedNodes.map((node) => node?.data).filter(Boolean));
  };

  const getRowId = useCallback((params) => params.data?.id ?? params.data?.value, []);

  const getRowClass = useCallback(
    (params) => {
      const id = params.data?.id ?? params.data?.value;
      return highlightedIds?.has(id) ? 'bg-success-25' : '';
    },
    [highlightedIds]
  );

  const noRowsOverlayComponent = useCallback(
    () => (
      <EmptyProviderState
        addProvider={addProvider}
        title={
          emptyTitle ||
          (isInNetwork
            ? 'No in-network providers added yet'
            : 'No out-of-network providers added yet')
        }
        description={
          emptyDescription ||
          (isInNetwork
            ? 'This provider has an agreement with certain insurance plans. Visits are typically covered at a lower cost when your insurance is in-network. If your plan is not in-network, you may be responsible for a higher portion of the cost.'
            : 'This provider does not have an agreement with your insurance plan. Your visit may not be fully covered, and you may be responsible for a higher copay or the full cost.')
        }
        addProviderButton={addProviderButton}
      />
    ),
    [enableSelection, isInNetwork, addProviderButton, emptyTitle, emptyDescription]
  );

  const useProvidersTableData = ({ params }) => {
    let filteredProviders = providers;

    if (params?.filters) {
      filteredProviders = filteredProviders.filter((provider) => {
        return Object.entries(params.filters).every(([column, value]) => {
          return value.every(({ formula, values }) => {
            const isContains = formula === 'contains';
            const providerValue = String(provider?.[column] ?? '').toLowerCase();
            const filterValue = String(values ?? '').toLowerCase();
            const includesValue = providerValue.includes(filterValue);

            return isContains ? includesValue : !includesValue;
          });
        });
      });
    }

    const data = { providers: filteredProviders, count: filteredProviders.length };

    return { data };
  };

  return (
    <div style={{ height: dynamicHeight }}>
      <AgTablePage
        noHeader
        noFilterActions
        name={enableSelection ? 'add-network-providers' : 'payer-network-providers'}
        key={defaultColumns.length}
        queryKey="providers"
        suppressMovableColumns={!enableSelection}
        suppressDragLeaveHidesColumns={!enableSelection}
        defaultColDef={{ suppressMenu: !enableSelection }}
        noDisplayButton={noFiltersAndDisplayButton}
        useData={useProvidersTableData}
        defaultColumns={defaultColumns}
        defaultFilters={!noFiltersAndDisplayButton ? PROVIDER_FILTERS : {}}
        onSelect={handleSelect}
        noRowsOverlayComponent={noRowsOverlayComponent}
        rowSelection={rowSelection}
        rowMultiSelectWithClick={enableSelection}
        suppressRowClickSelection={!enableSelection}
        getRowId={getRowId}
        getRowClass={getRowClass}
        {...props}
      />
    </div>
  );
};

export default ProvidersTable;
